/**
 * AvatarMenu Component
 * LAYER-SHELL: Unified user avatar menu
 *
 * User avatar with dropdown menu for account actions.
 * Role-aware for showing appropriate menu items.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import type { UserRole } from './Shell';
import './AvatarMenu.scss';

interface AvatarMenuProps {
  userRole: UserRole;
  /**
   * User info to display in the menu.
   * If not provided, will show generic avatar.
   */
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  /**
   * Callback when user logs out.
   * If not provided, will clear localStorage and redirect.
   */
  onLogout?: () => void;
}

/**
 * Get the account path based on role.
 */
function getAccountPath(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/accounts/details';
    default:
      return '/profile';
  }
}

/**
 * Get the settings path based on role.
 * Customers don't have a separate settings page — Account Details covers it.
 */
function getSettingsPath(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/accounts/details';
    default:
      return '/settings';
  }
}

export const AvatarMenu: React.FC<AvatarMenuProps> = ({
  userRole,
  user,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  // Get initials for avatar
  const initials = user
    ? `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase()
    : '';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleAccountClick = () => {
    setIsOpen(false);
    navigate(getAccountPath(userRole));
  };

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate(getSettingsPath(userRole));
  };

  const handleThemeToggle = () => {
    toggleTheme();
    // Keep menu open so user can see the change
  };

  const handleLogout = () => {
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      // Default behavior: clear localStorage and redirect
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  return (
    <div className="avatar-menu" ref={menuRef}>
      <button
        ref={buttonRef}
        className="avatar-menu__trigger"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Account menu"
      >
        {initials ? (
          <span className="avatar-menu__initials">{initials}</span>
        ) : (
          <UserCircleIcon className="avatar-menu__placeholder" />
        )}
      </button>

      {isOpen && (
        <div className="avatar-menu__dropdown" role="menu">
          {user && (
            <div className="avatar-menu__user-info">
              <span className="avatar-menu__user-name">
                {user.firstName} {user.lastName}
              </span>
              <span className="avatar-menu__user-email">{user.email}</span>
            </div>
          )}

          <div className="avatar-menu__divider" />

          <button
            className="avatar-menu__item"
            onClick={handleAccountClick}
            role="menuitem"
          >
            <UserCircleIcon className="avatar-menu__item-icon" />
            <span>Account</span>
          </button>

          <button
            className="avatar-menu__item"
            onClick={handleSettingsClick}
            role="menuitem"
          >
            <Cog6ToothIcon className="avatar-menu__item-icon" />
            <span>Settings</span>
          </button>

          {userRole !== 'CUSTOMER' && (
            <button
              className="avatar-menu__item"
              onClick={handleThemeToggle}
              role="menuitem"
            >
              {isDark ? (
                <SunIcon className="avatar-menu__item-icon" />
              ) : (
                <MoonIcon className="avatar-menu__item-icon" />
              )}
              <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
            </button>
          )}

          <div className="avatar-menu__divider" />

          <button
            className="avatar-menu__item avatar-menu__item--danger"
            onClick={handleLogout}
            role="menuitem"
          >
            <ArrowRightOnRectangleIcon className="avatar-menu__item-icon" />
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AvatarMenu;
